import React, { useState } from 'react';
import { BellIcon, SettingsIcon, CheckIcon } from 'lucide-react';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useRoleTheme } from '../hooks/useRoleTheme';
import { usePlatform } from '../context/PlatformContext';
import { NotificationCard } from '../components/Notifications/NotificationCard';
import { readerNotifications, vendorNotifications, notificationCategoryLabels, NotificationCategory } from '../data/notificationsData';
import { useUserRole } from '../context/UserRoleContext';
const Notifications = () => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const {
    isAfterTide
  } = usePlatform();
  const [activeCategory, setActiveCategory] = useState<NotificationCategory | 'all'>('all');
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  // Use role-specific notifications
  const initialNotifications = rt.isReader ? readerNotifications : vendorNotifications;
  const [notifications, setNotifications] = useState(initialNotifications);
  // Derived state
  const unreadCount = notifications.filter(n => !n.read).length;
  // Handlers
  const handleMarkRead = (id: string) => {
    setNotifications(notifications.map(n => n.id === id ? {
      ...n,
      read: true
    } : n));
  };
  const handleMarkAllRead = () => {
    setNotifications(notifications.map(n => ({
      ...n,
      read: true
    })));
  };
  const handleRemove = (id: string) => {
    setNotifications(notifications.filter(n => n.id !== id));
  };
  const handleAction = (id: string, actionType: 'approve' | 'decline') => {
    // In a real app, this would make an API call
    console.log(`${actionType} notification ${id}`);
    // Optimistically mark as read and remove actionable state
    setNotifications(notifications.map(n => n.id === id ? {
      ...n,
      read: true,
      actionable: false
    } : n));
  };
  // Filtering
  const filteredNotifications = notifications.filter(n => {
    if (showUnreadOnly && n.read) return false;
    if (activeCategory !== 'all' && n.category !== activeCategory) return false;
    return true;
  });
  // Group by date (simplified for mockup)
  const newNotifications = filteredNotifications.filter(n => !n.read);
  const earlierNotifications = filteredNotifications.filter(n => n.read);
  return <div className={`min-h-screen ${dk.page}`} data-id="element-12323" data-mp="19r4w38v2cpa8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-12324" data-mp="19sg099sf7z1v">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8 gap-4" data-id="element-12325" data-mp="1ooxafxovj7mu">
          <div className="flex items-center" data-id="element-12326" data-mp="1jo9mjkvxz6wu">
            <div className={`p-3 rounded-xl mr-4 ${isAfterTide ? 'bg-[#e2b04a]/10' : rt.bgMedium}`} data-id="element-12327" data-mp="303ausskf54y4">
              <BellIcon className={`h-6 w-6 ${rt.textPrimary}`} data-id="element-12328" data-mp="2v50yufqve0bg" />
            </div>
            <div data-id="element-12329" data-mp="32o0fidmy5dr4">
              <h1 className={`text-2xl font-bold ${dk.textHeading}`} data-id="element-12330" data-mp="32nh8vt2qybse">
                Notifications
              </h1>
              <p className={`text-sm mt-1 ${dk.textMuted}`} data-id="element-12331" data-mp="37nmtipalmy0z">
                You have {unreadCount} unread notification
                {unreadCount !== 1 ? 's' : ''}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-3" data-id="element-12332" data-mp="2xoxlrpcf2tyg">
            {unreadCount > 0 && <button onClick={handleMarkAllRead} className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${rt.secondaryButton}`} data-id="element-12333" data-mp="3lervvz5cq4f0">
                <CheckIcon className="h-4 w-4 mr-2" data-id="element-12334" data-mp="3cju3ct648to7" />
                Mark all as read
              </button>}
            <button className={`p-2 rounded-lg transition-colors ${dk.card} ${dk.border} hover:${dk.surfaceMuted} ${dk.textSecondary}`} title="Notification Settings" data-id="element-12335" data-mp="87q9uzct9rq2">
              <SettingsIcon className="h-5 w-5" data-id="element-12336" data-mp="2lf7z9l0purtw" />
            </button>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-8" data-id="element-12337" data-mp="2ggo8n7b6grv7">
          {/* Sidebar */}
          <div className="w-full lg:w-64 flex-shrink-0" data-id="element-12338" data-mp="2bhboiuboxywb">
            <div className={`rounded-xl border shadow-sm p-4 space-y-6 ${dk.card}`} data-id="element-12339" data-mp="2bi3cbiz1qsc1">
              {/* Filters */}
              <div data-id="element-12340" data-mp="345ze323h3afo">
                <h3 className={`text-sm font-semibold mb-3 ${dk.textPrimary}`} data-id="element-12341" data-mp="346ijxkfum8dc">
                  Filters
                </h3>
                <div className="space-y-2" data-id="element-12342" data-mp="2z87cfgd109ya">
                  <label className="flex items-center cursor-pointer group" data-id="element-12343" data-mp="2z98qltpc9ck8">
                    <input type="checkbox" checked={showUnreadOnly} onChange={e => setShowUnreadOnly(e.target.checked)} className={`rounded border-gray-300 ${rt.checkbox} focus:ring-offset-0`} data-id="element-12344" data-mp="2uecscbtidsyo" />
                    <span className={`ml-3 text-sm ${dk.textSecondary} transition-colors`} data-id="element-12345" data-mp="3i3npljbnh582">
                      Unread only
                    </span>
                  </label>
                </div>
              </div>

              <div className={`border-t ${dk.border}`} data-id="element-12346" data-mp="1dmr4nsh42g0z" />

              {/* Categories */}
              <div data-id="element-12347" data-mp="1in5dvdug7w0l">
                <h3 className={`text-sm font-semibold mb-3 ${dk.textPrimary}`} data-id="element-12348" data-mp="1iogjlj7gfduc">
                  Categories
                </h3>
                <div className="space-y-1" data-id="element-12349" data-mp="1nk5m9vertauv">
                  <button onClick={() => setActiveCategory('all')} className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded-md transition-colors ${activeCategory === 'all' ? rt.activeTab : `${dk.textSecondary}`}`} data-id="element-12350" data-mp="1sk9w52l6b7c4">
                    <span data-id="element-12351" data-mp="26d99oz1earh1">All Notifications</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${activeCategory === 'all' ? rt.badge : dk.badgeNeutral}`} data-id="element-12352" data-mp="26adbrapjs2sl">
                      {notifications.length}
                    </span>
                  </button>

                  {Object.entries(notificationCategoryLabels).map(([key, label]) => {
                  const count = notifications.filter(n => n.category === key).length;
                  if (count === 0) return null;
                  return <button key={key} onClick={() => setActiveCategory(key as NotificationCategory)} className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded-md transition-colors ${activeCategory === key ? rt.activeTab : `${dk.textSecondary}`}`} data-id="element-12353" data-mp="u1njov1nh510">
                          <span data-id="element-12354" data-mp="30qsgie49iz19">{label}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs ${activeCategory === key ? rt.badge : dk.badgeNeutral}`} data-id="element-12355" data-mp="30s3fm0mrjim1">
                            {count}
                          </span>
                        </button>;
                })}
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1" data-id="element-12356" data-mp="xhgwfry373dd">
            {filteredNotifications.length === 0 ? <div className={`flex flex-col items-center justify-center py-16 px-4 text-center rounded-xl border border-dashed ${dk.border} ${dk.card}`} data-id="element-12357" data-mp="12hcrh1ot3kgs">
                <BellIcon className={`h-12 w-12 mb-4 ${dk.textFaint}`} data-id="element-12358" data-mp="sidr06e72v4v" />
                <h3 className={`text-lg font-medium ${dk.textPrimary} mb-1`} data-id="element-12359" data-mp="skq8kig1j3he">
                  All caught up!
                </h3>
                <p className={`${dk.textMuted} max-w-sm`} data-id="element-12360" data-mp="2mx6lu3797hvr">
                  You don't have any notifications matching your current
                  filters.
                </p>
              </div> : <div className="space-y-8" data-id="element-12361" data-mp="15nykuewdqrqk">
                {newNotifications.length > 0 && <section data-id="element-12362" data-mp="10wqbtjyyp8dx">
                    <h2 className={`text-sm font-semibold uppercase tracking-wider mb-4 ${dk.textMuted}`} data-id="element-12363" data-mp="10vxbb0qljf48">
                      New
                    </h2>
                    <div className={`rounded-xl overflow-hidden border ${dk.border} ${dk.card} shadow-sm`} data-id="element-12364" data-mp="r0vls3sesb14">
                      <div className={`divide-y ${dk.divider}`} data-id="element-12365" data-mp="hye3g0fvk2je">
                        {newNotifications.map(notification => <NotificationCard key={notification.id} notification={notification} variant="full" onMarkRead={handleMarkRead} onAction={handleAction} onRemove={handleRemove} data-id="element-12366" data-mp="d0um2rnvr9n8" />)}
                      </div>
                    </div>
                  </section>}

                {earlierNotifications.length > 0 && <section data-id="element-12367" data-mp="2tlpv2t1c9ih4">
                    <h2 className={`text-sm font-semibold uppercase tracking-wider mb-4 ${dk.textMuted}`} data-id="element-12368" data-mp="2tky1fnraxuth">
                      Earlier
                    </h2>
                    <div className={`rounded-xl overflow-hidden border ${dk.border} ${dk.card} shadow-sm`} data-id="element-12369" data-mp="20y3hqt2b6zh2">
                      <div className={`divide-y ${dk.divider}`} data-id="element-12370" data-mp="2avhx0pchxi7s">
                        {earlierNotifications.map(notification => <NotificationCard key={notification.id} notification={notification} variant="full" onMarkRead={handleMarkRead} onAction={handleAction} onRemove={handleRemove} data-id="element-12371" data-mp="3haiks7rdau3a" />)}
                      </div>
                    </div>
                  </section>}
              </div>}
          </div>
        </div>
      </div>
    </div>;
};
export default Notifications;